import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Banknote,
  Shield,
  History,
  Download,
  Eye
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import VendorHistoryModal from '../../components/VendorHistoryModal';
import BlockVendorModal from '../../components/BlockVendorModal';

const VendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['vendor', id],
    async () => {
      const response = await api.get(`/vendors/${id}`);
      return response;
    },
    {
      enabled: !!id
    }
  );

  const approveVendorMutation = useMutation(
    (vendorId) => api.put(`/vendors/${vendorId}/approve`),
    {
      onSuccess: () => {
        toast.success('Vendor approved successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve vendor');
      }
    }
  );

  const blockVendorMutation = useMutation(
    (blockData) => api.put(`/vendors/${blockData.vendorId}/block`, blockData),
    {
      onSuccess: (data) => {
        toast.success(`Vendor ${data.isBlocked ? 'blocked' : 'unblocked'} successfully`);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update vendor status');
      }
    }
  );

  const deleteVendorMutation = useMutation(
    (vendorId) => api.delete(`/vendors/${vendorId}`),
    {
      onSuccess: () => {
        toast.success('Vendor deleted successfully');
        navigate('/vendors');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete vendor');
      }
    }
  );

  const toggleActiveMutation = useMutation(
    (vendorId) => api.put(`/vendors/${vendorId}/toggle-active`),
    {
      onSuccess: () => {
        toast.success('Vendor status updated successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update vendor status');
      }
    }
  );

  const handleApproveVendor = () => {
    approveVendorMutation.mutate(id);
  };

  const handleToggleActive = () => {
    toggleActiveMutation.mutate(id);
  };

  const handleBlockVendor = (blockData) => {
    blockVendorMutation.mutate({
      vendorId: id,
      ...blockData
    });
  };

  const handleDeleteVendor = () => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      deleteVendorMutation.mutate(id);
    }
  };

  const getStatusBadge = (vendor) => {
    if (vendor.isBlocked) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Blocked
        </span>
      );
    }

    if (!vendor.isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Pending Approval
        </span>
      );
    }

    if (!vendor.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading vendor details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Error Loading Vendor</h3>
        <p className="empty-state-description">
          {error.response?.data?.message || 'Failed to load vendor details'}
        </p>
        <Link to="/vendors" className="btn btn-primary mt-4">
          Back to Vendors
        </Link>
      </div>
    );
  }

  const vendor = data?.data?.data || data?.data;

  if (!vendor) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Vendor Not Found</h3>
        <p className="empty-state-description">
          The vendor you're looking for doesn't exist.
        </p>
        <Link to="/vendors" className="btn btn-primary mt-4">
          Back to Vendors
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => {
                  const searchParams = new URLSearchParams(window.location.search);
                  const fromSource = searchParams.get('from');
                  const sourceId = searchParams.get('sourceId');
                  
                  // Navigate back to the source page
                  if (fromSource === 'trip' && sourceId) {
                    navigate(`/trips/${sourceId}`);
                  } else {
                    navigate('/vendors');
                  }
                }}
                className="btn btn-outline"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
              <div>
                <h1 className="page-title">{vendor.VendorName}</h1>
                <p className="page-subtitle">Fleet ID: {vendor.fleetSysId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getStatusBadge(vendor)}
                {hasPermission('vendors', 'write') && vendor.isApproved && (
                  <button
                    onClick={handleToggleActive}
                    className={`btn btn-sm ${vendor.isActive ? 'btn-warning' : 'btn-success'}`}
                    disabled={toggleActiveMutation.isLoading}
                    title={vendor.isActive ? "Deactivate Vendor" : "Activate Vendor"}
                  >
                    {vendor.isActive ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    {vendor.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                {hasPermission('vendors', 'write') && !vendor.isApproved && (
                  <button
                    onClick={handleApproveVendor}
                    className="btn btn-success btn-sm"
                    disabled={approveVendorMutation.isLoading}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </button>
                )}

                {hasPermission('vendors', 'write') && (
                  <button
                    onClick={() => setShowBlockModal(true)}
                    className={`btn btn-sm ${vendor.isBlocked ? 'btn-warning' : 'btn-error'}`}
                    disabled={blockVendorMutation.isLoading}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {vendor.isBlocked ? 'Unblock' : 'Block'}
                  </button>
                )}

                <button
                  onClick={() => setShowHistoryModal(true)}
                  className="btn btn-secondary btn-sm"
                >
                  <History className="h-4 w-4 mr-2" />
                  History
                </button>

                {hasPermission('vendors', 'delete') && (
                  <button
                    onClick={handleDeleteVendor}
                    className="btn btn-error btn-sm"
                    disabled={deleteVendorMutation.isLoading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Building2 className="h-5 w-5 mr-2" />
                Basic Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Vendor Name:</span>
                  <span className="text-sm text-gray-900">{vendor.VendorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Fleet System ID:</span>
                  <span className="text-sm text-gray-900">{vendor.fleetSysId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Owner Name:</span>
                  <span className="text-sm text-gray-900">{vendor.OwnerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">UPI ID:</span>
                  <span className="text-sm text-gray-900">{vendor.UPIId || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Phone className="h-5 w-5 mr-2" />
                Contact Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Phone Number:</span>
                  <span className="text-sm text-gray-900">{vendor.ownerPhone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Email Address:</span>
                  <span className="text-sm text-gray-900">{vendor.ownerEmail}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <MapPin className="h-5 w-5 mr-2" />
                Address Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">City:</span>
                  <span className="text-sm text-gray-900">{vendor.city}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">State:</span>
                  <span className="text-sm text-gray-900">{vendor.state}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Country:</span>
                  <span className="text-sm text-gray-900">{vendor.country}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Full Address:</span>
                  <span className="text-sm text-gray-900">{vendor.fullAddress}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <FileText className="h-5 w-5 mr-2" />
                Business Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">GST Number:</span>
                  <span className="text-sm text-gray-900">{vendor.gst || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Company PAN Number:</span>
                  <span className="text-sm text-gray-900">{vendor.companyPANNumber || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Owner Proof:</span>
                  <span className="text-sm text-gray-900">{vendor.ownerProof || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Banknote className="h-5 w-5 mr-2" />
                Bank Details
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Bank Name:</span>
                  <span className="text-sm text-gray-900">{vendor.bankDetails?.bankName || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">IFSC Code:</span>
                  <span className="text-sm text-gray-900">{vendor.bankDetails?.ifsc || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Branch:</span>
                  <span className="text-sm text-gray-900">{vendor.bankDetails?.branch || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Account Holder Name:</span>
                  <span className="text-sm text-gray-900">{vendor.bankDetails?.accountHolderName || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Account Number:</span>
                  <span className="text-sm text-gray-900">{vendor.bankDetails?.accountNumber || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Shield className="h-5 w-5 mr-2" />
                Status Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Approval Status:</span>
                  <span className="text-sm text-gray-900">
                    {vendor.isApproved ? 'Approved' : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Active Status:</span>
                  <span className="text-sm text-gray-900">
                    {vendor.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Block Status:</span>
                  <span className="text-sm text-gray-900">
                    {vendor.isBlocked ? 'Blocked' : 'Not Blocked'}
                  </span>
                </div>
                {vendor.isBlocked && vendor.blockReason && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Block Reason:</span>
                    <span className="text-sm text-gray-900">{vendor.blockReason}</span>
                  </div>
                )}
                {vendor.approvedBy && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Approved By:</span>
                    <span className="text-sm text-gray-900">{vendor.approvedBy?.name || 'Unknown'}</span>
                  </div>
                )}
                {vendor.approvedOn && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Approved On:</span>
                    <span className="text-sm text-gray-900">
                      {new Date(vendor.approvedOn).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <FileText className="h-5 w-5 mr-2" />
                Documents
              </h3>
            </div>
            <div className="card-body">
              <div className="documents-grid">
                {Object.entries(vendor.documents || {}).map(([key, value]) => (
                  <div key={key} className="document-item">
                    <span className="document-label">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                    {value ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        <Eye className="w-4 h-4" />
                        View Document
                      </a>
                    ) : (
                      <span className="document-missing">Not uploaded</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Block Modal */}
      {showBlockModal && vendor && (
        <BlockVendorModal
          isOpen={showBlockModal}
          vendor={vendor}
          onClose={() => {
            setShowBlockModal(false);
          }}
          onBlock={handleBlockVendor}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <VendorHistoryModal
          isOpen={showHistoryModal}
          vendorId={vendor._id}
          vendorName={vendor.VendorName}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default VendorDetails; 